import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { getEnvironmentValue, ENVIRONMENT_VALUES } from '@shared/utils';
import { Logger } from '@shared/logger';
import { useErrorHandling } from '../utils/useErrorHandling';
import type { PatientProfileCreateResponse } from '../types/patientprofile/PatientProfileCreateResponse';

interface UseDoxyUrlResult {
  /** The constructed doxy.me URL, or null if not ready */
  doxyUrl: string | null;
  /** The base URL of the doxy.me instance */
  baseUrl: string | null;
  /** Error message if URL construction failed */
  errorMessage: string | null;
}

/**
 * @group Hooks
 * 
 * @summary
 * <blockquote>Hook that constructs a doxy.me waiting room URL from patient profile data.</blockquote>
 * 
 * @remarks
 * - Handles profile validation and redirects if no profile exists
 * - Constructs doxy.me URL with patient name and autocheckin parameters
 * - Handles errors gracefully and provides error messages
 * 
 * @param profile - Patient profile data
 * @param loading - Whether profile is still loading
 * 
 * @returns Object containing doxyUrl and errorMessage
 * 
 * @example
 * ```tsx
 * const { profile, loading } = usePatientProfile();
 * const { doxyUrl, errorMessage } = useDoxyUrl(profile, loading);
 * ```
 */
export function useDoxyUrl(
  profile: PatientProfileCreateResponse['profile'] | null | undefined,
  loading: boolean
): UseDoxyUrlResult {
  const router = useRouter();
  const { showErrorToast } = useErrorHandling();
  const [doxyUrl, setDoxyUrl] = useState<string | null>(null);
  const [baseUrl, setBaseUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // Check if user has profile
    if (!loading && !profile) {
      Logger.info('[useDoxyUrl] No profile found, redirecting to profile creation');
      router.push('/dashboard/patient-profile');
      return;
    }

    // Build doxy.me URL if profile exists
    if (profile && !loading) {
      // TEMPORARY FIX: Using getEnvironmentValue with required=false until feature request is implemented
      // See: feature-request-environment-variable-utilities.md
      const doxyUrlResult = getEnvironmentValue(ENVIRONMENT_VALUES.DOXY.NEXT_PUBLIC_DOXY_URL, false);
      
      doxyUrlResult.match({
        ok: (url) => {
          // Check if Doxy URL is configured
          if (!url) {
            const notConfiguredMessage = 'Video conferencing is not configured for this environment';
            setErrorMessage(notConfiguredMessage);
            setBaseUrl(null);
            setDoxyUrl(null);
            Logger.warn('[useDoxyUrl] Doxy URL not configured for this environment');
            showErrorToast(
              { errorCode: 'DOXY_NOT_CONFIGURED', errorMessage: notConfiguredMessage },
              'Configuration Warning'
            );
            return;
          }

          setBaseUrl(url);

          const patientName = `${profile.firstName} ${profile.lastName}`;
          const params = new URLSearchParams({
            patientName: patientName,
            username: patientName,
            autocheckin: 'true'
          });

          const constructedUrl = `${url}/live?${params.toString()}`;
          setDoxyUrl(constructedUrl);
          setErrorMessage(null);
          Logger.info('[useDoxyUrl] Waiting room URL constructed successfully');
        },
        err: (error) => {
          // This shouldn't happen with required=false, but handle it anyway
          setErrorMessage(error.errorMessage ?? null);
          setBaseUrl(null);
          setDoxyUrl(null);
          showErrorToast(error, 'Configuration Error');
          Logger.error('[useDoxyUrl] Failed to get environment variable', {
            errorCode: error.errorCode,
            errorMessage: error.errorMessage
          });
        }
      });
    }
  }, [profile, loading, router]);

  return { doxyUrl, baseUrl, errorMessage };
}

